import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Copy, Gift, Loader2, MessageCircle, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { getMyReferrals } from '../services/referrals';
import { formatDate } from '../utils/currencyFormatter';

// "Refiere y gana": the subscriber's personal link and how much it's saving
// them — rules live in backend services/referrals.py.
const Referrals = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    getMyReferrals()
      .then(setData)
      .catch((err) => setError(err.response?.data?.detail || 'No se pudo cargar tu programa de referidos'));
  }, []);

  const shareText = data
    ? `Te invito a Selltrix, la plataforma que uso para analizar la rentabilidad de mis ventas en Mercado Libre. Con mi enlace tienes ${data.friend_discount_pct}% de descuento en tu primer mes: ${data.link}`
    : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(data.link);
      setCopied(true);
      toast.success('Enlace copiado');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('No se pudo copiar, selecciona el enlace manualmente');
    }
  };

  const freeMonths = data && data.reward_pct >= 100;

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-card/60 backdrop-blur-xl border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-referrals"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold font-outfit text-foreground">Refiere y gana</h1>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-6 py-8">
        {!data && !error ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : error ? (
          <p className="text-center text-sm text-red-500 py-24">{error}</p>
        ) : (
          <div className="max-w-2xl space-y-6">
            <div className="bg-card/60 backdrop-blur-md border border-border rounded-2xl p-8">
              <div className="flex items-start gap-3 mb-4">
                <Gift className="w-6 h-6 text-green-500 shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold font-outfit">Comparte Selltrix y paga menos</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Por cada persona que se suscriba con tu enlace y siga activa, tienes un{' '}
                    <strong className="text-foreground">{data.reward_pct_per_referral}% de descuento</strong> en tu suscripción,
                    acumulable hasta {data.max_referrals} referidos. Con {data.max_referrals} referidos activos, Selltrix te sale gratis.
                    Tus referidos obtienen un <strong className="text-foreground">{data.friend_discount_pct}% de descuento</strong> en su primer mes.
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <input
                  readOnly
                  value={data.link}
                  onFocus={(e) => e.target.select()}
                  data-testid="referral-link"
                  className="flex-1 min-w-0 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
                <Button variant="outline" onClick={handleCopy} data-testid="copy-referral-link">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span className="ml-1 hidden sm:inline">{copied ? 'Copiado' : 'Copiar'}</span>
                </Button>
              </div>
              <Button
                className="mt-3 w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white"
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener')}
              >
                <MessageCircle className="w-4 h-4 mr-2" /> Compartir por WhatsApp
              </Button>
            </div>

            <div className="bg-card/60 backdrop-blur-md border border-border rounded-2xl p-8">
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-lg font-semibold font-outfit">Tu descuento</h3>
                <span className="text-3xl font-bold text-foreground" data-testid="referral-reward-pct">{data.reward_pct}%</span>
              </div>
              <Progress value={(Math.min(data.active_referrals, data.max_referrals) / data.max_referrals) * 100} />
              <p className="text-sm text-muted-foreground mt-2">
                {data.active_referrals} de {data.max_referrals} referidos activos.{' '}
                {freeMonths
                  ? '¡Tu próxima factura es gratis mientras se mantengan activos!'
                  : data.reward_pct > 0
                    ? `Se aplica en tu próxima factura mientras sigan activos.`
                    : 'Comparte tu enlace para empezar a ahorrar.'}
              </p>
              {!data.reward_applies && data.active_referrals > 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  Tu plan actual no se cobra con tarjeta, así que el descuento se aplicará cuando tengas una suscripción de pago.
                </p>
              )}
            </div>

            <div className="bg-card/60 backdrop-blur-md border border-border rounded-2xl p-8">
              <h3 className="text-lg font-semibold font-outfit mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" /> Tus referidos
              </h3>
              {data.referrals.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aún nadie se ha registrado con tu enlace.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {data.referrals.map((r, i) => (
                    <li key={i} className="flex items-center justify-between py-3 gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{r.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{r.email} · desde {formatDate(r.since)}</p>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          r.is_active
                            ? 'bg-green-500/15 text-green-600 dark:text-green-400'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {r.is_active ? 'Activo' : r.has_subscribed ? 'Inactivo' : 'Sin suscripción'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Referrals;
